import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDashboardSummary,
  selectDashboard,
} from "../../slice/dashboard/Dashboardslice";

const formatINR = (amount) => {
  const num = Number(amount || 0);
  return `$${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const todayLabel = () => {
  const d = new Date();
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

/* ── Icon set (inline SVG, no external icon lib needed) ── */
const Icon = {
  revenue: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      <path d="M2 11h20" />
    </svg>
  ),
  orders: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <path d="M3 6h18" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
  vendors: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l1-5h16l1 5" />
      <path d="M3 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
      <path d="M4 9v10a1 1 0 0 0 1 1h4v-6h6v6h4a1 1 0 0 0 1-1V9" />
    </svg>
  ),
  customers: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

const ArrowUpRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

const StatCard = ({ icon, value, label, subtext }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #eef0f2",
      borderRadius: 14,
      padding: 20,
      display: "flex",
      flexDirection: "column",
      gap: 14,
      minWidth: 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: "#f4f5f7",
          color: "#374151",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </div>
      <ArrowUpRight />
    </div>

    <div>
      <div style={{ fontSize: 26, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
        {value}
      </div>
      <div style={{ fontSize: 14, color: "#374151", marginTop: 4 }}>{label}</div>
      {subtext && (
        <div style={{ fontSize: 12.5, color: "#9ca3af", marginTop: 4 }}>{subtext}</div>
      )}
    </div>
  </div>
);

const Dashboard = () => {
  const dispatch = useDispatch();

const {
  totalRevenue,
  revenueThisMonth,
  totalOrders,
  ordersToday,
  activeVendors,
  pendingVendors,
  totalCustomers,
  newCustomersThisMonth,
  loading,
} = useSelector(selectDashboard);

  const fetchAll = () => {
    // 🔧 CHANGED: only one API call now — summary already returns everything the cards need
    dispatch(fetchDashboardSummary());
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const cards = [
{
  icon:    Icon.revenue,
  value:   formatINR(totalRevenue),
  label:   "Total Revenue",
  subtext: `${formatINR(revenueThisMonth)} this month`,
},
 {
  icon:    Icon.orders,
  value:   totalOrders,
  label:   "Total Orders",
  subtext: `${ordersToday} today`,
},
    {
      icon:    Icon.vendors,
      value:   activeVendors,
      label:   "Active Vendors",
      subtext: `${pendingVendors} pending review`,
    },
    {
      icon:    Icon.customers,
      value:   totalCustomers,
      label:   "Customers",
      subtext: `${newCustomersThisMonth} new this month`,
    },
  ];

  return (
    <div className="container-fluid">
      <div style={{ padding: "24px 4px 8px" }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>
          Dashboard
        </h2>
        <div style={{ fontSize: 14, color: "#9ca3af", marginTop: 4, display: "flex", alignItems: "center", gap: 12 }}>
          <span>{todayLabel()}</span>
          <button
            onClick={fetchAll}
            disabled={loading}
            style={{
              border: "none",
              background: "transparent",
              color: "#6362e7",
              fontSize: 13,
              cursor: "pointer",
              padding: 0,
            }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 16,
          padding: "8px 4px 24px",
        }}
      >
        {cards.map((card) => (
          <StatCard key={card.label} {...card} />
        ))}
      </div>
    </div>
  );
};

export default Dashboard;